<script setup>
import Boxes from '@/mini/components/Boxes.vue'
import Box from '@/mini/components/Box.vue'
import Button from '@/mini/components/Button.vue'
import Sep from '@/mini/components/Sep.vue'
import Space from '@/mini/components/Space.vue'
import Modal from '@/mini/components/Modal.vue'
import CardFormFields from '@/components/CardFormFields.vue'
import { ref, reactive, watch } from 'vue'
import { useMessage } from '@/mini/composables/useMessage'
import { useCardtrader, resolveBlueprintForCard } from '@/composables/useCardtrader'
import { useCardForm } from '@/composables/useCardForm'
import {
  allowedSetTypes,
  normalizeSetType,
  buildFullSetId,
  composeCardId,
  normalizeApiSetId,
} from '@/utils/cardFormUtils'

import { useAuthStore } from '@/mini/stores/auth'
import { BACKEND_ORIGIN } from '@/config/api'

const authStore = useAuthStore()

const props = defineProps({
  visible: { type: Boolean, default: false },
  card: { type: Object, default: null },
  availableGroups: { type: Array, default: () => [] },
})

const emit = defineEmits(['close', 'saved', 'deleted'])

const isUpdating = ref(false)
const isDeleting = ref(false)
const isResettingLowestPrice = ref(false)
const error = ref('')
const lowestCtPrice = ref(null)
const isLoadingCtPrice = ref(false)
const activeMultiplier = ref(null)

const form = reactive({
  id: null,
  card_id: '',
  card_name: '',
  set_type: '',
  set_id: '',
  card_number: '',
  card_color: '',
  card_type: '',
  rarity: '',
  foil: 'FALSE',
  fruit: '',
  inventory_price: '',
  market_price: '',
  real_price: '',
  purchase_price: '',
  card_image: '',
  cardtrader_blueprint: null,
  cardtrader_expansion: null,
  cardtrader_product_id: null,
  cardtrader_sell_price: null,
  cardtrader_sell_quantity: null,
  cardtrader_market_price: null,
  cardtrader_market_price_fetched_at: null,
  cardtrader_lowest_market_price: null,
  to_buy: false,
  favourite: false,
  to_follow: false,
  sell_to_sell: false,
  sell_quantity_to_sell: 1,
  sell_folder: '',
  sell_page: '',
  group_entries: [],
  group_entries_deleted: [],
})

const {
  authenticatedApiFetch,
  composedCardId,
  isFetchingData,
  fetchCandidates,
  selectedFetchIndex,
  fetchCardData,
  importSelected,
} = useCardForm(form, { preserveApiIds: true })

async function apiFetch(path, options = {}) {
  const response = await authenticatedApiFetch(path, options)
  if (!response.ok) {
    let message = `Request failed (${response.status})`
    try {
      const ct = response.headers.get('content-type') || ''
      if (ct.includes('application/json')) {
        const body = await response.json()
        message = body?.detail || body?.message || body?.error || JSON.stringify(body)
      } else {
        const text = await response.text()
        if (text) message = text
      }
    } catch {}
    throw new Error(message)
  }
  return response
}

function populate(card) {
  if (!card) return
  form.id = card.id
  form.card_name = card.card_name || ''
  form.set_type = (card.set_type || '').toUpperCase()
  form.card_number = card.card_number === undefined || card.card_number === null ? '' : String(card.card_number)
  form.set_id = form.set_type === 'P' ? '' : normalizeApiSetId(card.set_id, form.set_type)
  form.card_id = composeCardId(form.set_type, form.set_id, form.card_number) || card.card_id || ''
  form.card_color = card.card_color || ''
  form.card_type = card.card_type || ''
  form.rarity = card.rarity || ''
  form.foil = card.foil === true || card.foil === 'TRUE' ? 'TRUE' : 'FALSE'
  form.fruit = card.fruit || ''
  form.inventory_price = card.inventory_price ?? ''
  form.market_price = card.market_price ?? ''
  form.real_price = card.real_price ?? ''
  form.purchase_price = card.purchase_price ?? ''
  form.card_image = card.card_image || ''
  form.cardtrader_blueprint = card.cardtrader_blueprint ?? null
  form.cardtrader_expansion = card.cardtrader_expansion ?? null
  form.cardtrader_product_id = card.cardtrader_product_id ?? null
  form.cardtrader_sell_price = card.cardtrader_sell_price ?? null
  form.cardtrader_sell_quantity = card.cardtrader_sell_quantity ?? null
  form.cardtrader_market_price = card.cardtrader_market_price ?? null
  form.cardtrader_market_price_fetched_at = card.cardtrader_market_price_fetched_at ?? null
  form.cardtrader_lowest_market_price = card.cardtrader_lowest_market_price ?? null
  form.to_buy = card.to_buy === true
  form.favourite = card.favourite === true
  form.to_follow = card.to_follow === true
  form.sell_to_sell = card.sell_to_sell === true
  form.sell_quantity_to_sell = Number(card.sell_quantity_to_sell) || 1
  form.sell_folder = card.sell_folder ? String(card.sell_folder) : ''
  form.sell_page = card.sell_page ? String(card.sell_page) : ''
  form.group_entries = (card.group_entries ?? []).map(e => ({
    id: e.id,
    group_name: e.group_name ?? '',
    quantity: Number(e.quantity) || 0,
    folder: e.storage_folder ?? '',
    page: e.storage_page ?? '',
    warehouse: e.warehouse ?? false,
  }))
  if (!form.group_entries.length) {
    form.group_entries.push({ id: null, group_name: '', quantity: 0, folder: '', page: '', warehouse: false })
  }
  form.group_entries_deleted = []
}

watch(() => props.card, (card) => {
  if (card) {
    error.value = ''
    fetchCandidates.value = []
    selectedFetchIndex.value = 0
    lowestCtPrice.value = null
    populate(card)
  }
}, { immediate: true })

function close() {
  populate(props.card)
  error.value = ''
  fetchCandidates.value = []
  selectedFetchIndex.value = 0
  emit('close')
}

async function save() {
  error.value = ''
  if (!form.id) {
    error.value = 'Missing card ID.'
    return
  }
  const cardId = String(form.card_id || '').trim() || composedCardId.value
  if (!cardId || !form.card_name.trim()) {
    error.value = 'Set type, Card number and card name are required.'
    return
  }
  const setType = normalizeSetType(form.set_type, cardId)
  if (!allowedSetTypes.includes(setType)) {
    error.value = 'Set type is required and must be one of: ST, P, OP, EB, PRB.'
    return
  }
  isUpdating.value = true
  try {
    const mainPayload = {
      card_id: cardId,
      card_name: form.card_name.trim(),
      set_type: setType,
      set_id: buildFullSetId(setType, form.set_id),
      card_number: form.card_number.trim(),
      card_color: form.card_color.trim(),
      card_type: form.card_type.trim(),
      rarity: form.rarity.trim(),
      foil: form.foil === 'TRUE',
      fruit: form.fruit.trim(),
      card_image: form.card_image.trim(),
    }
    const optcgPayload = {
      inventory_price: form.inventory_price === '' ? null : Number(form.inventory_price),
      market_price: form.market_price === '' ? null : Number(form.market_price),
    }
    const cardtraderPayload = {
      blueprint: form.cardtrader_blueprint || null,
      expansion: form.cardtrader_expansion || null,
    }
    const collectionPayload = {
      real_price: form.real_price === '' ? null : Number(form.real_price),
      purchase_price: form.purchase_price === '' ? null : Number(form.purchase_price),
      to_buy: form.to_buy,
      favourite: form.favourite,
      to_follow: form.to_follow,
      sell_to_sell: form.sell_to_sell,
      sell_quantity_to_sell: Number(form.sell_quantity_to_sell) || 1,
      sell_storage_folder: String(form.sell_folder || '').trim(),
      sell_storage_page: String(form.sell_page || '').trim(),
    }

    await apiFetch(`/api/v1/cards/${form.id}/`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(mainPayload),
    })
    await apiFetch(`/api/v1/cards/${form.id}/optcg/`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(optcgPayload),
    })
    await apiFetch(`/api/v1/cards/${form.id}/cardtrader/`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(cardtraderPayload),
    })
    await apiFetch(`/api/v1/cards/${form.id}/collection/`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(collectionPayload),
    })

    // Delete removed entries
    for (const entryId of form.group_entries_deleted) {
      await apiFetch(`/api/v1/cards/${form.id}/groups/${entryId}/`, { method: 'DELETE' })
    }

    // Update existing / create new entries
    for (const entry of form.group_entries) {
      const entryPayload = {
        group_name: entry.group_name.trim(),
        quantity: Number(entry.quantity) || 0,
        storage_folder: String(entry.folder || '').trim(),
        storage_page: String(entry.page || '').trim(),
        warehouse: entry.warehouse,
      }
      if (entry.id) {
        await apiFetch(`/api/v1/cards/${form.id}/groups/${entry.id}/`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(entryPayload),
        })
      } else if (entry.group_name.trim() || entry.folder.trim() || entry.page.trim() || Number(entry.quantity) > 0) {
        await apiFetch(`/api/v1/cards/${form.id}/groups/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(entryPayload),
        })
      }
    }

    emit('saved')
    close()
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Unable to update card'
  } finally {
    isUpdating.value = false
  }
}


async function deleteCard() {
  if (!form.id) return
  if (!confirm(`Delete "${form.card_name || form.card_id}"? This cannot be undone.`)) return
  error.value = ''
  isDeleting.value = true
  try {
    const response = await authenticatedApiFetch(`/api/v1/cards/${form.id}/`, { method: 'DELETE' })
    if (!response.ok) throw new Error('Unable to delete card')
    emit('deleted', form.id)
    close()
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Unable to delete card'
  } finally {
    isDeleting.value = false
  }
}

const { showInfoMessage, showSuccessMessage, showWarningMessage, showDangerMessage } = useMessage()
const {
  isFetchingBlueprints: isFetchingCardtrader,
  isCheckingListing: isCheckingCardtrader,
  isSelling,
  isSellPriceUpdating,
  isDeletingListing,
  fetchCardtraderDataForCard,
  refetchExpansionForCard,
  checkListing,
  createListing,
  deleteListing,
  updateListing,
} = useCardtrader(authenticatedApiFetch)

function checkCardtraderListing() { return checkListing(form) }
function sellOnCardtrader() { return createListing(form) }
async function deleteCardtraderListing() { await deleteListing(form); emit('saved') }
async function updateSellPrice() { await updateListing(form, form.cardtrader_sell_price, form.cardtrader_sell_quantity); emit('saved') }

async function fetchCardtraderInfo() {
  if (isFetchingCardtrader.value) return
  isFetchingCardtrader.value = true
  try {
    if (form.cardtrader_blueprint) {
      // Re-fetch: blueprint known — resolve expansion from local extensions cache
      const cardId = String(form.card_id || '').trim() || composedCardId.value
      const fullSetId = form.set_id ? `${form.set_type}${form.set_id}`.toUpperCase() : null
      if (!fullSetId) { showWarningMessage('Set ID is required.'); return }
      const { expansionId } = await resolveBlueprintForCard(fullSetId, cardId)
      if (!expansionId) { showWarningMessage('No expansion found for this set in cached extensions.'); return }
      await apiFetch(`/api/v1/cards/${form.id}/cardtrader/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ expansion: expansionId }),
      })
      form.cardtrader_expansion = expansionId
      showSuccessMessage(`Expansion #${expansionId} saved.`)
    } else {
      // Initial fetch: resolve blueprint + expansion from set_id / card_id
      if (!form.set_id) { showWarningMessage('Set ID is required to fetch CardTrader info.'); return }
      const cardId = String(form.card_id || '').trim() || composedCardId.value
      if (!cardId) { showWarningMessage('Card ID is required to fetch CardTrader info.'); return }
      const entity = { ...form, card_id: cardId }
      const { blueprintId } = await fetchCardtraderDataForCard(entity)
      form.cardtrader_blueprint = entity.cardtrader_blueprint
      form.cardtrader_expansion = entity.cardtrader_expansion
      if (blueprintId) showSuccessMessage('CardTrader info saved.')
      else if (entity.cardtrader_expansion) showWarningMessage('Expansion found but no matching blueprint.')
      else showWarningMessage('No CardTrader expansion found for this card.')
    }
  } catch (err) {
    showDangerMessage(err instanceof Error ? err.message : 'Failed to fetch CardTrader info.')
  } finally {
    isFetchingCardtrader.value = false
  }
}

function setMultiplier(mult) {
  const base = form.real_price ? Number(form.real_price) : Number(form.market_price)
  form.cardtrader_sell_price = Math.round(base * mult * 100) / 100
  activeMultiplier.value = mult
}

async function resetLowestPrice() {
  if (!form.id || isResettingLowestPrice.value) return
  isResettingLowestPrice.value = true
  try {
    await apiFetch(`/api/v1/cards/${form.id}/cardtrader/`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ lowest_market_price: null, lowest_market_price_fetched_at: null }),
    })
    form.cardtrader_lowest_market_price = null
    showSuccessMessage('Lowest price reset.')
  } catch (err) {
    showDangerMessage(err instanceof Error ? err.message : 'Failed to reset lowest price.')
  } finally {
    isResettingLowestPrice.value = false
  }
}

async function fetchLowestCtPrice() {
  if (!form.cardtrader_blueprint || isLoadingCtPrice.value) return
  isLoadingCtPrice.value = true
  lowestCtPrice.value = null
  try {
    const res = await authStore.authFetch(`${BACKEND_ORIGIN}/api/v1/ct/marketplace/?blueprint_id=${form.cardtrader_blueprint}`)
    if (!res.ok) throw new Error(`${res.status}`)
    const data = await res.json()
    const products = Object.values(data).flat()
    const filtered = products.filter(p =>
      p.properties_hash?.condition === 'Near Mint' &&
      p.properties_hash?.onepiece_language === 'jp' &&
      p.user?.can_sell_sealed_with_ct_zero === true
    )
    if (filtered.length) {
      const price = Math.min(...filtered.map(p => p.price_cents)) / 100
      lowestCtPrice.value = price
      const previousPrice = form.cardtrader_market_price != null ? Number(form.cardtrader_market_price) : null
      const previousFetchedAt = form.cardtrader_market_price_fetched_at ?? null
      const storedLowest = form.cardtrader_lowest_market_price != null ? Number(form.cardtrader_lowest_market_price) : null
      const trend = previousPrice != null ? (price > previousPrice ? 'up' : price < previousPrice ? 'down' : 'equal') : null
      const shouldUpdateLowest = storedLowest == null || price < storedLowest
      const nowIso = new Date().toISOString()
      const patch = {
        market_price: price,
        market_price_fetched_at: nowIso,
        ...(trend != null ? { price_trend: trend } : {}),
        ...(previousPrice != null && price !== previousPrice ? { prev_market_price: previousPrice, prev_market_price_fetched_at: previousFetchedAt } : {}),
        ...(shouldUpdateLowest ? { lowest_market_price: price, lowest_market_price_fetched_at: nowIso } : {}),
      }
      await authenticatedApiFetch(`/api/v1/cards/${form.id}/cardtrader/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      })
      form.cardtrader_market_price = price
      form.cardtrader_market_price_fetched_at = nowIso
      if (trend) form.cardtrader_price_trend = trend
      if (shouldUpdateLowest) form.cardtrader_lowest_market_price = price
      showSuccessMessage(`Lowest NM/JP price saved: ${price.toFixed(2)}€`)
    } else {
      lowestCtPrice.value = -1
      showInfoMessage('No NM/JP listings found on CardTrader.')
    }
  } catch (err) {
    lowestCtPrice.value = null
    showDangerMessage(err instanceof Error ? err.message : 'Failed to fetch CT price.')
  } finally {
    isLoadingCtPrice.value = false
  }
}

</script>

<template>
  <Modal :visible="visible" @close="close">
    <Boxes>
      <Box :size="100">
        <h2 class="m-0">Edit card</h2>
      </Box>
      <Box :size="100">
        <form class="create-card-form" @submit.prevent="save">
          <Boxes gap="0">
            <CardFormFields
              :form="form"
              :composedCardId="composedCardId"
              :isFetchingData="isFetchingData"
              :fetchCandidates="fetchCandidates"
              :selectedFetchIndex="selectedFetchIndex"
              :availableGroups="props.availableGroups"
              idPrefix="edit-"
              mode="edit"
              @fetch="fetchCardData"
              @import="importSelected"
              @update:selectedFetchIndex="val => (selectedFetchIndex = val)"
            />
            <Sep class="mb-15"/>
            <Box size="100" class="blue-border b-rad-10 border-1 blue-border p-2">
              <Boxes>
                <Box size="100" class="p-0">
                  <h3 class="blue-text h5 m-0">CardTrader<span v-if="form.cardtrader_product_id" class="flag success-box b-rad-5 ms-05">On sale</span></h3>
                </Box>
                <Box size="33" class="p-0">
                  <p class="flex align-items-center">
                    <span class="label">Expansion:</span>&nbsp;
                    <input v-model.number="form.cardtrader_expansion" type="number" step="1" min="0" class="S p-05 blue-text" style="width: 110px;" placeholder="-" />
                  </p>
                </Box>
                <Box size="33" class="p-0">
                  <p class="flex align-items-center">
                    <span class="label">Blueprint:</span>&nbsp;
                    <input v-model.number="form.cardtrader_blueprint" type="number" step="1" min="0" class="S p-05 blue-text" style="width: 110px;" placeholder="-" />
                  </p> 
                </Box>
                <Box size="33" class="p-0">
                  <p v-if="form.cardtrader_blueprint && lowestCtPrice !== null" class="m-0">
                    <span v-if="lowestCtPrice === -1" class="label">No NM/JP listings found</span>
                  </p>
                </Box>
                <Box v-if="form.cardtrader_product_id" size="66" class="p-0">
                  <p class="flex align-items-center">
                    <label class="label me-1 mb-0 bold">Sell price</label>
                    <input type="number" step="0.01" min="0" v-model.number="form.cardtrader_sell_price" class="S p-05 me-05" style="width: 80px;" placeholder="?" />
                    <span class="S label mb-0">€</span>
                    <span class="mx-2 light-grey-text">|</span>
                    <label class="S label me-05 mb-0">Qty</label>
                    <input type="number" step="1" min="1" v-model.number="form.cardtrader_sell_quantity" class="S p-05" style="width: 60px;" placeholder="?" />
                  </p>
                </Box>
                <Box v-if="form.cardtrader_product_id"size="33" class="p-0">
                  <p class="">
                    <span class="label">ID:</span> <b class="blue-text">{{ form.cardtrader_product_id ?? '-' }}</b>
                  </p>
                </Box>
                <Box v-if="form.cardtrader_product_id && (form.market_price || form.real_price)" size="100" class="p-0">
                  <p class="">
                    <Button v-for="mult in [0.5, 0.75, 0.9, 1, 1.1, 1.25, 1.5, 2, 3, 4, 5, 10]" :key="mult" size="XS" color="blue" :invert="activeMultiplier !== mult" class="round px-05" @click="setMultiplier(mult)">{{ mult }}x</Button>
                  </p>
                </Box>
                <Box v-if="!form.cardtrader_blueprint" size="100" class="pb-0 px-0">
                  <p class="m-0">
                    <Button color="blue" invert class="px-15 round" size="S" :disabled="isFetchingCardtrader" @click="fetchCardtraderInfo">
                      {{ isFetchingCardtrader ? 'Fetching...' : 'Fetch CardTrader' }}
                    </Button>
                  </p>
                </Box>
                <Box v-if="form.cardtrader_blueprint" size="100" class="pb-0 px-0">
                  <p class="m-0">
                    <Button v-if="form.cardtrader_blueprint" size="S" color="blue" class="px-15 round" :link="`https://www.cardtrader.com/en/cards/${form.cardtrader_blueprint}`" target="_blank">
                      View on CT ↗
                    </Button>
                    <Button color="blue" invert class="px-15 round" size="S" :disabled="isFetchingCardtrader" @click="fetchCardtraderInfo">
                      {{ isFetchingCardtrader ? 'Fetching...' : (form.cardtrader_blueprint ? 'Fetch extension' : 'Fetch CardTrader info') }}
                    </Button>
                    <Button v-if="form.cardtrader_blueprint" size="S" color="blue" class="px-15 round" :disabled="isLoadingCtPrice" @click="fetchLowestCtPrice">
                      {{ isLoadingCtPrice ? 'Fetching price...' : 'Check CT price' }}
                    </Button>
                    <Button v-if="form.cardtrader_lowest_market_price != null" size="S" color="danger" invert class="px-15 round" :disabled="isResettingLowestPrice" @click="resetLowestPrice">
                      {{ isResettingLowestPrice ? 'Resetting...' : `Reset lowest (${Number(form.cardtrader_lowest_market_price).toFixed(2)}€)` }}
                    </Button>
                    <Button
                      v-if="form.cardtrader_blueprint && (lowestCtPrice > 0 || form.cardtrader_market_price)"
                      size="S" color="color" invert class="px-15 round"
                      @click="form.real_price = lowestCtPrice > 0 ? lowestCtPrice : Number(form.cardtrader_market_price)"
                    >
                      Set real price = CT ({{ (lowestCtPrice > 0 ? lowestCtPrice : Number(form.cardtrader_market_price)).toFixed(2) }}€)
                    </Button>
                    <Button v-if="form.cardtrader_blueprint && form.sell_to_sell && !form.cardtrader_product_id" size="S" color="success" class="px-15 round" :disabled="isSelling" @click="sellOnCardtrader">
                      {{ isSelling ? 'Listing...' : 'Sell now!' }}
                    </Button>
                  </p>
                </Box>
                <Box v-if="form.cardtrader_product_id" size="100" class="pb-0 px-0">
                  <p class="m-0">
                    <Button v-if="form.cardtrader_product_id" size="S" color="blue" invert class="px-10 round" :disabled="isCheckingCardtrader" @click="checkCardtraderListing">
                      {{ isCheckingCardtrader ? 'Checking...' : 'Check sell status' }}
                    </Button>
                    <Button v-if="form.cardtrader_product_id" size="S" color="danger" invert class="px-10 round" :disabled="isDeletingListing" @click="deleteCardtraderListing">
                      {{ isDeletingListing ? 'Removing...' : 'Remove listing' }}
                    </Button>
                    <Button v-if="form.cardtrader_product_id" size="S" color="blue" class="px-1 round" :disabled="isSellPriceUpdating" @click="updateSellPrice">
                      {{ isSellPriceUpdating ? 'Updating...' : 'Update listing' }}
                    </Button>
                  </p>
                </Box>
              </Boxes>
            </Box>
            <Box size="100">
              <Button color="light-grey" @click="close" class="round px-15">Cancel</Button>
              <Button color="third-color" @click="deleteCard" :disabled="isDeleting" class="round px-15">
                {{ isDeleting ? 'Deleting...' : 'Delete' }}
              </Button>
              <Button color="second-color" @click="save" :disabled="isUpdating" class="round px-15">
                {{ isUpdating ? 'Saving...' : 'Save changes' }}
              </Button>
            </Box>
          </Boxes>
          <p v-if="error" class="bad-text S m-0">{{ error }}</p>
        </form>
      </Box>
    </Boxes>
  </Modal>
</template>
